<script>
import { defineComponent } from 'vue';

export default defineComponent({
    name: 'affiliateMapCard',

    props: {
        affiliate: {
            type: Object,
            required: true,
        },
    },

    emits: ['show-contact-form'],

    data() {
        return {
            showContactForm: false,
        };
    },

    computed: {
        deltaU() {
            const delta = this.affiliate._deltaU;
            if (delta) {
                return delta > 50000 ? Math.round(delta / 1000) : (Math.round(delta / 100) / 10);
            } else {
                return null;
            }
        }
    },

    methods: {
        getGoogleDirectionsUrl() {
            return 'https://maps.google.com/maps/search/' + encodeURIComponent(this.affiliate.address)
        },
    },
});
</script>

<template>
    <div class="affiliate">
        <div class="header vm-row">
            <div class="vm-col">
                <h3 class="title">
                    <span>{{ affiliate.title }}</span>
                    <span v-if="affiliate._deltaU" class="proximity">{{ deltaU }}km</span>
                </h3>

                <a class="address" :href="getGoogleDirectionsUrl()" target="_blank">
                    <div v-html="affiliate.address.replace('\n', '<br/>')"></div>
                </a>
            </div>
            <div class="vm-col">
                <div class="type">{{ affiliate.types.map(t => $str(`${$affiliateTypes[t]}.singular`)).join('/') }}</div>
            </div>
        </div>

        <div class="info vm-row">

            <div class="vm-col">
                <a
                    class="infoLine for--phone"
                    v-if="affiliate.contact.phone"
                    :href="`tel:${affiliate.contact.phone.replace(/[^\d]/g, '')}`"
                    target="_blank"
                >
                    <i class="icon fa-regular fa-phone fa-fw" aria-hidden="true"></i>
                    <span>{{ affiliate.contact.phone }}</span>
                </a>

                <a
                    class="infoLine for--email"
                    v-if="affiliate.contact.email"
                    :href="`mailto:${affiliate.contact.email}`"
                    target="_blank"
                >
                    <i class="icon fa-regular fa-envelope fa-fw" aria-hidden="true"></i>
                    <span>{{ affiliate.contact.email }}</span>
                </a>

                <a
                    class="infoLine for--homeUrl"
                    v-if="affiliate.contact.website"
                    :href="affiliate.contact.website"
                    target="_blank"
                >
                    <i class="icon fa-regular fa-globe fa-fw" aria-hidden="true"></i>
                    <span>{{ affiliate.contact.website.replace(/^(?:https?:)?\/\/(?:www\.)?(.*)$/, '$1') }}</span>
                </a>
            </div>

            <div class="vm-col">
                <button class="contactBtn" v-if="affiliate.contact.email" @click="$emit('show-contact-form', affiliate)">
                    <i class="icon fa-regular fa-paper-plane" aria-hidden="true"></i>
                </button>
            </div>
        </div>

    </div>
</template>

<style lang="scss" scoped>
@import "../defs";

.affiliate {
    padding: 2.4rem 3.2rem;
    font-size: 1.6rem;
    font-weight: 400;
    background-color: $c_lightest;
    border-radius: 5px;

    a {
        color: $c_dark;
        font-weight: 400;
        text-decoration: underline;
        text-decoration-color: transparent;
        text-underline-offset: 0.2em;
        &:hover, &:focus {
            color: black;
            text-decoration-color: $c_primary;
        }
    }

    .vm-row {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        .vm-col {
            &:first-child {
                width: 75%;
            }
            &:last-child {
                display: flex;
                align-items: flex-end;
                justify-content: flex-end;
                width: 20%;
            }
        }
    }

    .header {
        padding-bottom: 1.6rem;
        .title {
            margin: 0;
            font-size: 2.4rem;
            font-weight: 800;
            color: black;
            .proximity {
                margin-left: 0.75rem;
                font-size: 0.75em;
                color: $c_gray;
            }
        }
        .type {
            font-size: 1.8rem;
            font-weight: 300;
            color: $c_gray;
        }
        .address {
            position: relative;
            margin: 1.5rem 0 0;
            padding: 0 1rem;

            div {
                display: inline-block;
                position: relative;
                z-index: 1;
            }

            &::after {
                content: "";
                display: block;
                position: absolute;
                z-index: 0;
                top: 0;
                left: 0;
                width: 4px;
                height: 100%;
                background-color: $c_primary;
                transition: $transition_fast;
            }

            &:hover {
                color: black;

                &::after {
                    width: 100%;
                }
            }
        }
    }

    .info {
        padding-top: 1.6rem;
        border-top: 3px solid $c_light;
        .contactBtn {
            margin: 0;
            padding: 0.75rem 3rem;
            font-size: 1.8rem;
            background-color: $c_primary;
            border-radius: 5px;
            &:hover, &:focus {
                background-color: $c_primary_lighter;
            }
        }
        .infoLine {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            padding: 0.25em 0;
            .icon {
                margin-right: 8px;
                text-decoration: none !important;
            }
        }
    }
}

</style>
